import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useAuthClient} from '../utils/use-auth-client'

const dropdownStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 20,
  marginTop: '2px',
  maxHeight: '220px',
  overflowY: 'auto',
  background: '#fff',
  border: '1px solid #bfbfbf',
  borderRadius: '5px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  fontFamily: 'Open Sans',
  fontSize: '12px',
}

const itemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid #f0f0f0',
  padding: '8px',
  cursor: 'pointer',
}

const stateStyle = {padding: '8px', color: '#6b6b6b'}

const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 400

/**
 * Loqate-backed address typeahead (Find/Retrieve proxied through ooe-service so
 * the Loqate key is never exposed). Rendered only for Loqate countries; the
 * caller keeps the plain input for every other country.
 *
 * - `value` / `onChange(text)`: the street text, wired to the form field.
 * - `onSelect({street, city, state, postalCode})`: fired after a full address is
 *   retrieved so the caller can fill the remaining fields.
 */
export const AddressSearchInput = ({
  value,
  onChange,
  onSelect,
  onBlur,
  country,
  disabled,
  style,
  placeholder,
}) => {
  const client = useAuthClient()
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [failed, setFailed] = useState(false)

  const wrapperRef = useRef(null)
  // Set after a selection fills the field, so the debounced search below doesn't
  // immediately re-search the address it was just given.
  const justSelectedRef = useRef(false)
  const query = value || ''

  const fetchSuggestions = useCallback(
    async (text, container) => {
      const params = new URLSearchParams({text})
      if (container) params.set('container', container)
      if (country) params.set('country', country)
      const response = await client(`accounts/address/find?${params}`)
      return Array.isArray(response) ? response : []
    },
    [client, country],
  )

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false
      return undefined
    }
    if (disabled || query.length < MIN_QUERY_LENGTH) {
      setResults([])
      return undefined
    }

    let cancelled = false
    const timeout = setTimeout(async () => {
      try {
        setLoading(true)
        setFailed(false)
        const data = await fetchSuggestions(query)
        if (!cancelled) setResults(data)
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setResults([])
          setFailed(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [query, disabled, fetchSuggestions])

  useEffect(() => {
    const handleOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  const handleSelect = async item => {
    // Container results (street / postcode groupings) need another Find
    // drill-down; only Type === 'Address' can be retrieved.
    if (item.Type !== 'Address') {
      try {
        setLoading(true)
        setFailed(false)
        setResults(await fetchSuggestions(query, item.Id))
      } catch (err) {
        console.error(err)
        setResults([])
        setFailed(true)
      } finally {
        setLoading(false)
      }
      return
    }

    try {
      setLoading(true)
      setFailed(false)
      const resolved = await client(
        `accounts/address/retrieve?id=${encodeURIComponent(item.Id)}`,
      )
      justSelectedRef.current = true
      onChange(resolved.street || '')
      onSelect?.(resolved)
      setResults([])
      setOpen(false)
    } catch (err) {
      console.error(err)
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div ref={wrapperRef} style={{position: 'relative'}}>
      <input
        type="text"
        autoComplete="off"
        placeholder={placeholder}
        style={style}
        value={query}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onChange={e => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') setOpen(false)
        }}
      />
      {open && !disabled && query.length >= MIN_QUERY_LENGTH && (
        <div style={dropdownStyle}>
          {loading && <div style={stateStyle}>Searching...</div>}
          {!loading &&
            results.map(item => (
              <button
                key={item.Id}
                type="button"
                style={itemStyle}
                onClick={() => handleSelect(item)}
              >
                <div>{item.Text}</div>
                {item.Description && (
                  <div style={{color: '#6b6b6b'}}>{item.Description}</div>
                )}
              </button>
            ))}
          {!loading && failed && (
            <div style={stateStyle}>
              Address lookup is unavailable. Please enter the address manually.
            </div>
          )}
          {!loading && !failed && results.length === 0 && (
            <div style={stateStyle}>No results found</div>
          )}
        </div>
      )}
    </div>
  )
}
